import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import TrophyBadge from './TrophyBadge'
import { CATEGORIES_ORDER } from './constants'
import ProgressRing from './ProgressRing'
import IconChevronRight from '@/shared/icons/Gamification/IconChevronRight'
import {
  SectionWrapper,
  SectionTitle,
  SectionSubtitle,
  SectionSubtitle2,
  LevelRow,
  LevelItem,
  LevelBadgeWrapper,
  LevelChevron,
  CategoryDotsRow,
} from './styled-components'
import useModal from '@/shared/hooks/useModal'
import { Modal, ModalBody, ModalHeader } from 'baseui/modal'

// ── Category badge SVGs (completed / disabled) ─────────────────────────────
const categoryBadgeModules = import.meta.glob('@/shared/icons/Gamification/*-{completed,disabled}.svg', {
  eager: true,
  import: 'default',
})

const getCategoryBadgeSrc = (categoryId, achieved) => {
  const state = achieved ? 'completed' : 'disabled'
  const match = Object.entries(categoryBadgeModules).find(([path]) => path.endsWith(`/${categoryId}-${state}.svg`))
  return match ? match[1] : null
}

const BADGE_ICON_SIZE = 20

const LevelOverviewSection = ({ levelSummaries, categories }) => {
  const { t } = useTranslation('gamification')
  const { isOpen, setIsOpen } = useModal()

  return (
    <SectionWrapper>
      <SectionTitle>{t('levelOverviewTitle')}</SectionTitle>
      <SectionSubtitle>{t('levelOverviewSubtitle')}</SectionSubtitle>
      <SectionSubtitle2 onClick={() => setIsOpen(true)}>{t('levelOverviewHowItWorks')}</SectionSubtitle2>
      <LevelRow>
        {levelSummaries.map((level, index) => {
          const isComplete = level.count >= level.total
          const reached = level.count > 0

          let overlayContent
          if (isComplete) {
            overlayContent = 'check'
          } else if (reached) {
            overlayContent = `${level.count}x`
          } else {
            overlayContent = '0x'
          }

          const bannerVariant = isComplete ? 'completed' : reached ? 'default' : 'disabled'

          return (
            <Fragment key={level.levelKey}>
              {index > 0 && (
                <LevelChevron>
                  <IconChevronRight style={{ width: '1.5rem', height: '1.5rem' }} />
                </LevelChevron>
              )}
              <LevelItem>
                <LevelBadgeWrapper>
                  <ProgressRing progress={level.progress} />
                  <TrophyBadge
                    categoryId={null}
                    trophyColor={level.levelKey}
                    dimmed={!reached}
                    numberOverlay={overlayContent}
                    bannerLabel={level.levelKey === 'starter' ? null : t(`level_${level.levelKey}`)}
                    bannerVariant={bannerVariant}
                  />
                </LevelBadgeWrapper>
                <CategoryDotsRow>
                  {CATEGORIES_ORDER.map((cat, idx) => {
                    const category = categories.find(c => c.id === cat.id)
                    const achieved = category ? category.currentLevelOrder >= level.order : false
                    const src = getCategoryBadgeSrc(cat.id, achieved)
                    return src ? (
                      <img
                        key={cat.id}
                        src={src}
                        alt=''
                        width={BADGE_ICON_SIZE}
                        height={BADGE_ICON_SIZE}
                        style={{ marginLeft: idx > 0 ? '-0.7rem' : 0 }}
                      />
                    ) : null
                  })}
                </CategoryDotsRow>
              </LevelItem>
            </Fragment>
          )
        })}
      </LevelRow>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        overrides={{
          Dialog: {
            style: {
              width: '80vw',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <ModalHeader>{t('levelOverviewHowItWorks')}</ModalHeader>
        <ModalBody>
          <p dangerouslySetInnerHTML={{ __html: t('levelOverviewHowItWorksContent') }} />
        </ModalBody>
      </Modal>
    </SectionWrapper>
  )
}

export default LevelOverviewSection
